import { ref, computed, watch, nextTick } from 'vue'

/**
 * Shared composable for comment threading, reply state management,
 * and active-comment scroll behaviour used by both
 * ReviewCommentPanel and ExternalCommentPanel.
 *
 * @param {Object} options
 * @param {import('vue').Ref<Array>|import('vue').ComputedRef<Array>} options.comments - reactive list of comment objects
 * @param {import('vue').Ref<string>|import('vue').ComputedRef<string>} options.itemType - 'video' | 'image' etc.
 * @param {import('vue').Ref<string|null>|import('vue').ComputedRef<string|null>} options.activeCommentId - currently highlighted comment id
 * @param {import('vue').Ref<HTMLElement|null>} options.commentListRef - template ref for the scrollable comment list container
 * @param {import('vue').Ref<string>|import('vue').ComputedRef<string>} [options.sortMode] - 'timecode' (default) | 'recent'
 */
export function useCommentThreads({ comments, itemType, activeCommentId, commentListRef, sortMode }) {
  // --- Reply state ---
  const replyingToCommentId = ref(null)
  const replyText = ref('')
  const replyTextareaRef = ref(null)

  async function startReplyMode(comment) {
    replyingToCommentId.value = comment.id
    replyText.value = ''
    await nextTick()
    if (replyTextareaRef.value) {
      const el = Array.isArray(replyTextareaRef.value) ? replyTextareaRef.value[0] : replyTextareaRef.value
      el?.focus()
    }
  }

  function cancelReply() {
    replyingToCommentId.value = null
    replyText.value = ''
  }

  /**
   * Submit a reply via the provided emit function.
   * Emits 'submit-reply' with `{ parentCommentId, text }`.
   */
  function submitReply(emit) {
    if (!replyText.value?.trim() || !replyingToCommentId.value) return
    emit('submit-reply', {
      parentCommentId: replyingToCommentId.value,
      text: replyText.value.trim(),
    })
    cancelReply()
  }

  // --- Threaded comments ---
  const threadedComments = computed(() => {
    const list = comments.value ?? comments
    const topLevel = list.filter(c => !c.parentId)
    const repliesByParent = {}

    for (const c of list) {
      if (c.parentId) {
        if (!repliesByParent[c.parentId]) repliesByParent[c.parentId] = []
        repliesByParent[c.parentId].push(c)
      }
    }

    const mode = sortMode?.value ?? sortMode ?? 'timecode'
    if (mode === 'recent') {
      // Newest first, by creation time — works for video and image items.
      // Pending optimistic writes have createdAt: null (serverTimestamp not yet
      // resolved) → createdAtMs is 0; treat missing/0 as newest so a just-posted
      // comment shows at the top instead of briefly sinking to the bottom.
      const key = c => c.createdAtMs || Infinity
      topLevel.sort((a, b) => key(b) - key(a))
    } else if ((itemType.value ?? itemType) === 'video') {
      topLevel.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0))
    }

    return topLevel.map(comment => ({
      ...comment,
      replies: repliesByParent[comment.id] || [],
    }))
  })

  // --- Scroll to active comment ---
  watch(
    () => (activeCommentId.value ?? activeCommentId),
    async (id) => {
      if (!id) return
      await nextTick()
      const listEl = commentListRef.value
      if (!listEl || typeof listEl.querySelector !== 'function') return
      const el = listEl.querySelector('.comment-item.is-active')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    },
  )

  return {
    replyingToCommentId,
    replyText,
    replyTextareaRef,
    startReplyMode,
    cancelReply,
    submitReply,
    threadedComments,
  }
}
